import React, { useState, useEffect, useRef } from "react";
import { useSelector } from "react-redux";

// SVG Imports
import BackToTop from "../../../../assets/landingPage/TopArrow.svg";
import TeamPicture from "../../../../assets/landingPage/TeamPicture.jpg";
import designElement1 from "../../../../assets/landingPage/designElement1.svg";
import designElement2 from "../../../../assets/landingPage/designElement2.svg";
import designElement3 from "../../../../assets/landingPage/designElement3.svg";

// MUI Imports
import CircularProgress from "@mui/material/CircularProgress";
import Typography from "@mui/material/Typography";

// COMPONENTS Import
import Footer from "../../../../components/LandingPage/Footer";
import ICONS from "../../../../assets/Icons";

const AboutUs = () => {
  // useRef
  const containerRef = useRef(null);

  // useState
  const [isVisible, setIsVisible] = useState(false);
  const [scrollPercent, setScrollPercent] = useState(0);

  // useSelector
  const { mobilestatus, webstatus } = useSelector(
    (state) => state.landingpagestate,
  );

  const handleCardScroll = () => {
    if (containerRef.current) {
      const cards = containerRef.current.children;
      const containerScrollLeft = containerRef.current.scrollLeft;
      let closestDistance = Infinity;

      for (let i = 0; i < cards.length; i++) {
        const card = cards[i];
        const cardOffsetLeft = card.offsetLeft - containerScrollLeft;
        const distance = Math.abs(cardOffsetLeft);

        if (distance < closestDistance) {
          closestDistance = distance;
        }
      }
    }
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // useEffect
  useEffect(() => {
    if (!containerRef.current) return;
    const element = containerRef.current;
    element.addEventListener("scroll", handleCardScroll);
    return () => {
      element.removeEventListener("scroll", handleCardScroll);
    };
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 20) {
        setIsVisible(true);
      } else {
        setIsVisible(false);
      }
    };
    window.addEventListener("scroll", handleScroll);
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  useEffect(() => {
    window.addEventListener("scroll", () => {
      let scrollTop = window.scrollY;
      let docHeight = document.documentElement.scrollHeight;
      let winHeight = window.innerHeight;
      let scrollPercentt = scrollTop / (docHeight - winHeight);
      let scrollPercentRounded = Math.round(scrollPercentt * 100);
      setScrollPercent(scrollPercentRounded);
    });
  }, [scrollPercent]);

  function CircularProgressWithLabel(props) {
    return (
      <div sx={{ position: "relative", display: "inline-flex" }}>
        <CircularProgress
          size="59px"
          sx={{ color: "var(--pending)" }}
          variant="determinate"
          {...props}
        />
        <div
          sx={{
            zIndex: 999,
            position: "absolute",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Typography
            variant="caption"
            component="div"
            sx={{ color: "text.secondary" }}
          >
            <img
              src={BackToTop}
              alt="back to top"
              width="50"
              style={{ right: "45px", position: "fixed", bottom: "34px" }}
            />
          </Typography>
        </div>
      </div>
    );
  }
  const journeyItems = [
    {
      image: ICONS?.FINANCIAL_MANAGEMENT,
      alt: "FinancialManagement",
      title: "Identifying gaps in corporate\nfinancial management",
      content: [
        "• At Quest2Pay, we noticed that corporate cards only worked for vendors with POS terminals or payment gateways.",
        "• We introduced virtual cards to enable payments for vendors without card acceptance infrastructure.",
        "• Despite this, businesses with multiple bank accounts struggled with fragmented systems and lacked visibility over their finances.",
      ],
    },
    {
      image: ICONS?.CONSOLIDATION,
      alt: "Consolidation",
      title: "The need for\nconsolidation",
      content: [
        "• Businesses required a unified platform to manage multiple bank accounts and financial activities.",
        "• They needed real-time visibility, control, and optimisation across all accounts.",
        "• This realisation laid the foundation for Xpentra.",
      ],
    },
    {
      image: ICONS?.BIRTH_OF_XPENTRA,
      alt: "BirthOfXpentra",
      title: "The birth of\nXpentra",
      content: [
        "• Xpentra was developed to provide a single portal for managing bank accounts and streamlining financial operations.",
        "• It offers a bird’s-eye view of financial activities, enabling smarter and faster decisions.",
        "• Our mission: To empower businesses with better control, real-time insights, and solutions for modern financial challenges.",
      ],
    },
    {
      image: ICONS?.EMPOWERING_FINANCIAL_OPERATIONS_BLUE,
      alt: "FinancialOperations",
      title: "Xpentra today: Empowering\nfinancial operations",
      content: [
        "• Xpentra offers real-time transaction visibility, multi-bank integration, and advanced analytics.",
        "• The platform is designed for scalability, flexibility, and security, catering to modern business needs.",
        "• It helps businesses optimise resources, reduce inefficiencies, and drive growth.",
      ],
    },
  ];

  const evolutionItems = [
    {
      year: "2016",
      title: "The vision begins",
      side: "right",
      image: ICONS?.VISION_DARK_BLUE,
      alt: "VisionBegins",
      content: [
        "• Xpentra was conceptualised to address the growing need for an all-in-one financial management platform.",
      ],
      contentClass: "",
    },
    {
      year: "2017",
      title: "Expanding functionality",
      side: "left",
      image: ICONS?.EXPANDING_FUNCTIONALITY,
      alt: "ExpandFunctionality",
      content: [
        "• Added refunds and reimbursements features for smoother financial operations.",
      ],
      contentClass: "lg:mb-2.5",
    },
    {
      year: "2018",
      title: "Enhancing connectivity",
      side: "right",
      image: ICONS?.ENHANCING_CONNECTIVITY,
      alt: "EnhanceConnectivity",
      content: [
        "• Integrated API connectivity for seamless data exchange with third-party platforms.",
        "• Introduced virtual accounts for more efficient transaction management.",
      ],
      contentClass: "mb-1 last:mb-0",
    },
    {
      year: "2019",
      title: "Reliable verifications",
      side: "left",
      image: ICONS?.RELIABLE_VERIFICATIONS,
      alt: "ReliableVerifications",
      content: [
        "• Introduced real-time verifications to ensure secure and accurate transactions.",
      ],
      contentClass: "",
    },
    {
      year: "2020",
      title: "NACH collections",
      side: "right",
      image: ICONS?.NACH_COLLECTIONS,
      alt: "NACHCollections",
      content: [
        "• Added functionality for NACH collections, simplifying automated payment collections.",
      ],
      contentClass: "",
    },
    {
      year: "2021",
      title: "Secure management",
      side: "left",
      image: ICONS?.SECURE_MANAGEMENT,
      alt: "SecureManagement",
      content: [
        "• Introduced easy management and addition of beneficiaries to streamline payments.",
        "• Added real-time balances and statement view for faster financial insights.",
      ],
      contentClass: "",
    },
    {
      year: "2022",
      title: "Optimising for business",
      side: "right",
      image: ICONS?.OPTIMISING_FOR_BUSINESS,
      alt: "OptimisingBusiness",
      content: [
        "• Introduced a customisable authorisation matrix for secure, flexible user permissions.",
        "• Added transaction categories to help businesses manage and classify financial activities more effectively.",
      ],
      contentClass: "",
    },
    {
      year: "2023",
      title: "Expanding with Bharat Connect",
      side: "left",
      image: ICONS?.BHARAT_CONNECT,
      alt: "BharatConnect",
      content: [
        "• Integrated with the Bharat Bill Payment System (now Bharat Connect) to offer broader payment solutions.",
        "• Launched clean-up of bank narration, improving financial clarity for businesses.",
      ],
      contentClass: "",
    },
    {
      year: "2024",
      title: "Specialised solutions",
      side: "right",
      image: ICONS?.SPECIALISED_SOLUTIONS,
      alt: "SpecialisedSolutions",
      content: [
        "• Delivered BFSI-specific solutions, tailoring our platform for the banking, financial services, and insurance industries.",
        "• Added GST + TDS payment functionality, simplifying tax-related payments.",
        "• Began offering our end-to-end customisable reconciliation solution, enhancing accuracy and reducing manual effort.",
      ],
      contentClass: "",
    },
  ];

  return (
    <>
      <div>
        {isVisible && (
          <div
            onClick={scrollToTop}
            className="fixed bottom-[25px] right-[40px] cursor-pointer z-20"
          >
            <CircularProgressWithLabel value={scrollPercent} />
          </div>
        )}
        <div
          style={{
            overflow: webstatus ? "hidden" : "",
            overflowY: mobilestatus ? "hidden" : "",
          }}
        >
          {/* About Xpentra */}
          <div
            className="lg:flex lg:relative text-text-primary bg-[#102C5D] p-[30px] mt-[60px]
          lg:pt-[60px] lg:px-0 lg:pb-0 lg:mt-0"
          >
            <div
              className="bg-[#102C5D] rounded-[15px] mx-auto flex flex-col
              lg:flex-row w-full gap-[20px] lg:gap-5 lg:h-[492px] lg:max-w-[1025px]
              lg:w-screen lg:justify-between lg:relative"
            >
              <div className="flex flex-col gap-5 lg:my-auto lg:gap-0">
                <div className="text-lg lg:leading-[unset] font-semibold lg:text-font-xl lg:mb-[26px]">
                  About Xpentra
                </div>

                <div className="bg-grey w-full lg:w-[467px] lg:h-[287px]">
                  <img
                    className="rounded-[10px] lg:rounded-[8px]"
                    src={TeamPicture}
                    alt="TeamPicture"
                  />
                </div>
              </div>
              <div
                className="w-full flex flex-col justify-center text-text-primary text-font-xs
                lg:w-[537px] lg:text-font-sm"
              >
                <p className="mb-2.5 lg:mb-0">
                  Xpentra is an innovative fintech venture founded by the
                  Rangnekar family, who bring a legacy of industry success,
                  drawing on their significant contributions to Quest2Travel-a
                  corporate travel management company acquired by the
                  NASDAQ-listed MakeMyTrip in 2018.
                </p>
                <br className="hidden lg:block" />
                <p className="mb-2.5 lg:mb-0">
                  With over 2 decades of experience and taking Quest2travel to
                  greater heights, the founders have now ventured into the
                  Fintech Space with an understanding of the complexities of the
                  industry.
                </p>
                <br className="hidden lg:block" />
                <p className="mb-2.5 lg:mb-0">
                  Founded in 2018, Xpentra is built to deliver a comprehensive
                  banking solution for enterprises, offering superior
                  transaction and account visibility and streamlining complex
                  financial processes like payments, collections, reporting, and
                  reconciliation.
                </p>
                <br className="hidden lg:block" />
                <p>
                  Xpentra prioritizes usability and protection, featuring
                  automated transactions, With Xpentra, businesses can reduce
                  operational errors, providing CFOs and CXOs with a powerful
                  enabler that eliminates time consuming administrative tasks
                  and allowing leaders to focus on strategic initiatives and
                  high-impact decision making.
                </p>
              </div>
              <div className="hidden lg:block absolute h-[62px] w-[65px] ml-[150px] mt-[30px]">
                <img
                  src={designElement3}
                  alt="designElement3"
                />
              </div>
              <div className="hidden lg:block absolute h-[62px] w-[65px] ml-[50px] mt-[395px]">
                <img
                  src={designElement3}
                  alt="designElement3"
                />
              </div>
            </div>

            <div className="hidden lg:block absolute h-[100px] w-[218px] left-[-60px] top-[25%]">
              <img
                src={designElement1}
                alt="designElement1"
              />
            </div>

            <div className="hidden lg:block absolute h-[62px] w-[65px] right-[50px] mt-[75px]">
              <img
                src={designElement3}
                alt=""
              />
            </div>

            <div className="hidden lg:block absolute h-[62px] w-[65px] right-[0] mt-[225px] rotate-90">
              <img
                src={designElement2}
                alt=""
              />
            </div>

            <div className="hidden lg:block absolute h-[62px] w-[65px] right-[25px] mt-[375px]">
              <img
                src={designElement2}
                alt=""
              />
            </div>
          </div>

          {/* Our journey */}
          <div className="text-text-primary py-[20px] px-[30px] bg-[#031f4f] lg:py-[30px] lg:px-0">
            <div className="mx-auto max-w-[1024px]">
              <div
                className="text-font-md lg:text-font-xl font-semibold text-text-primary pb-[20px]
                lg:text-center"
              >
                Our <span className="text-pending">journey</span>
              </div>

              <div className="relative w-full">
                <div
                  className="absolute border-pending border-l lg:border-l-0 lg:border-b h-[75%] lg:h-0
                  top-[37px] lg:top-[40px] left-[25px] lg:left-[40px] lg:w-[80%]"
                ></div>
                <div className="flex flex-col lg:flex-row justify-between gap-[20px] lg:gap-[50px]">
                  {journeyItems.map((item) => (
                    <div
                      key={item.alt}
                      className="flex lg:flex-col gap-[20px] lg:gap-0"
                    >
                      <div className="w-[50px] h-[50px] flex-shrink-0 lg:w-[80px] lg:h-[80px] lg:mb-5">
                        <img
                          src={item.image}
                          alt={item.alt}
                          className="relative z-1 lg:w-[80px] lg:h-[80px]"
                        />
                      </div>
                      <div>
                        <div
                          className="title-section font-semibold text-font-md lg:text-sm mb-[5px] whitespace-pre-line
                        lg:mb-2.5"
                        >
                          {item.title}
                        </div>

                        {item.content.map((text, index) => (
                          <div
                            key={index}
                            className="text-font-xs lg:text-font-xxs mb-[5px] last:mb-0
                            lg:mb-2.5 lg:last:mb-0"
                          >
                            {text}
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Our evolution */}
          <div
            className="text-text-primary py-[20px] px-[30px] lg:px-0
            bg-[linear-gradient(270deg,_#031F4F_0%,_#1C3A6D_27%,_#224074_78%,_#031F4F_100%,_#234176_100%)]
            lg:bg-[linear-gradient(90deg,_#031F4F_0%,_#224074_50%,_#031F4F_100%)]"
          >
            <div className="mx-auto max-w-[1024px]">
              {/* Heading */}
              <div
                className="text-font-md lg:text-font-xl font-semibold text-text-primary
               pb-[20px] lg:text-center"
              >
                <span className="text-pending">Our evolution:</span> Key feature
                additions over the years
              </div>

              <div className="relative w-full">
                {/* Timeline */}
                <div
                  className="absolute border-pending border-l h-[84%] top-[25px]
                 left-[25px] lg:h-[95%] lg:top-0 lg:left-1/2"
                />

                <div className="flex flex-col justify-between gap-[20px] lg:gap-[40px]">
                  {evolutionItems.map((item) => {
                    const isLeft = item.side === "left";

                    return (
                      <div
                        key={item.year}
                        className="flex flex-row gap-5 lg:items-center
                     lg:justify-between lg:gap-0"
                      >
                        {/* LEFT / CONTENT */}
                        <div
                          className={`w-full lg:w-[45%] order-2
                        ${isLeft ? "lg:order-1 lg:text-right" : "lg:order-3"} `}
                        >
                          <div>
                            <div
                              className="title-section font-semibold text-font-md
                              lg:text-sm mb-1"
                            >
                              <span className="text-pending">{item.year}</span>{" "}
                              – {item.title}
                            </div>

                            {item.content.map((text, index) => (
                              <div
                                key={index}
                                className={`text-font-xs lg:text-font-xxs 
                                ${item.contentClass}
                              `}
                              >
                                {text}
                              </div>
                            ))}
                          </div>
                        </div>

                        {/* ICON */}
                        <div
                          className="order-1 flex justify-center flex-shrink-0
                          w-[50px] lg:order-2 lg:items-center lg:m-auto lg:w-[10%]"
                        >
                          <img
                            src={item.image}
                            alt={item.alt}
                            className="w-[50px] lg:w-[80px] h-[50px] lg:h-[80px] 
                          relative z-1 rounded-[50%]"
                          />
                        </div>

                        {/* EMPTY SIDE - DESKTOP ONLY */}
                        <div
                          className={`hidden lg:block lg:w-[45%]
                          ${isLeft ? "lg:order-3" : "lg:order-1"}
                        `}
                        />
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        </div>
        <Footer />
      </div>
    </>
  );
};

export default AboutUs;
